import React, { FC } from 'react';

import { useIntl } from 'react-intl';

import Incrementor from 'components/incrementor';
import MenuLineItem from 'components/menu-order-wizard/menu-line-item';
import PriceAndCalories from 'components/price-and-calories';
import { useFormatJoules } from 'hooks/use-format-joules';
import useMediaQuery from 'hooks/use-media-query';
import { LaunchDarklyFlag, useFlag } from 'state/launchdarkly';
import { useUIContext } from 'state/ui';
import { curriedModifierMultiplierNameFromIndex } from 'utils/menu/modifiers';

import { IMenuOptionStepperProps } from './types';

const MenuModifierStepper: FC<IMenuOptionStepperProps> = ({
  calories,
  image,
  index,
  itemOption,
  name,
  onChange,
  price,
  max,
}) => {
  const { formatMessage } = useIntl();
  const { formatCurrencyForLocale } = useUIContext();
  const isDesktop = useMediaQuery('desktop');
  const isTinyMobile = useMediaQuery('mobileTiny');

  const enableEUNutritionDisplay = useFlag(LaunchDarklyFlag.ENABLE_EU_NUTRITION_DISPLAY);
  const formatJoules = useFormatJoules();

  let valueWidth = '4.2rem';
  if (isTinyMobile) {
    valueWidth = '3.4375rem';
  } else if (isDesktop) {
    valueWidth = '5rem';
  }

  const showPrice = price > 0;
  const formattedPrice = showPrice ? `+${formatCurrencyForLocale(price)}` : '';
  const formattedJoules = formatJoules(Number(calories?.split(' ')[0]));

  const euFormattedNutritionText = formatMessage(
    { id: 'euNutritionValues' },
    { calories, joules: formattedJoules }
  );

  const globalizedCalorieFormat = calories ? `${euFormattedNutritionText}` : '';
  const finalCalories = enableEUNutritionDisplay ? globalizedCalorieFormat : calories;

  const details = (
    <PriceAndCalories
      isOffer={false}
      currentPrice={formattedPrice}
      shouldHideCalories={!calories}
      formattedCalories={finalCalories}
      priceTestId="modifier-upcharge"
    />
  );

  const modifierMultiplierValsFromIndex = itemOption
    ? curriedModifierMultiplierNameFromIndex(itemOption)
    : (v: number) => v;

  const maxOptions = itemOption?.options?.length ? itemOption?.options?.length - 1 : 0;

  return (
    <MenuLineItem
      data-testid={name}
      name={name}
      details={details}
      highlightOnHover={false}
      ignoreLabelClicks
      image={image}
      indicator={
        <Incrementor
          aria-label={formatMessage({ id: 'quantityOf' }, { name })}
          data-testid={name}
          name={name}
          value={index}
          valueDisplayTransformer={modifierMultiplierValsFromIndex}
          onChange={value => onChange(value)}
          min={0}
          max={max ?? maxOptions}
          valueWidth={valueWidth}
        />
      }
    />
  );
};

export default MenuModifierStepper;
